<template>
  <div class="login-page">
    <div class="login-card" :class="{ 'login-card--otp': otpSent }">
      <img
        class="login-logo"
        :src="rcsLogo"
        alt="RCS Consulting Solutions"
      />
      <p class="step-title">Sign in to your account</p>

      <form class="login-form" @submit.prevent="onSubmit">
        <div
          class="field"
          :class="{ 'field--focused': emailFocused, 'field--filled': !!identifier }"
        >
          <label class="field-label" for="identifier">Email</label>
          <div class="input-wrap">
            <span class="input-icon" aria-hidden="true">
              <svg class="icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M4 6.5h16a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 20 17.5H4A1.5 1.5 0 0 1 2.5 16V8A1.5 1.5 0 0 1 4 6.5Z"
                  stroke="currentColor"
                  stroke-width="1.7"
                />
                <path
                  d="m3.5 8 8.5 6 8.5-6"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            <input
              id="identifier"
              ref="emailInput"
              v-model.trim="identifier"
              class="field-input"
              type="email"
              autocomplete="username"
              :disabled="sending || loggingIn"
              :readonly="otpSent"
              @focus="emailFocused = true"
              @blur="emailFocused = false"
              @keydown.enter.prevent="otpSent ? null : sendOtp()"
            />
          </div>
        </div>

        <transition name="otp-slide">
          <div v-if="otpSent" key="otp" class="otp-section">
            <label class="otp-label">Enter the 6-digit code</label>
            <div class="otp-row" role="group" aria-label="One-time password">
              <input
                v-for="(_, index) in otpDigits"
                :key="index"
                :ref="(el) => setOtpRef(el, index)"
                class="otp-input"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                maxlength="1"
                autocomplete="one-time-code"
                :placeholder="otpDigits[index] ? '' : ''"
                :value="otpDigits[index]"
                :disabled="loggingIn"
                @input="onOtpInput($event, index)"
                @keydown="onOtpKeydown($event, index)"
                @paste="onOtpPaste"
              />
            </div>

            <p class="expiry-row">
              <span class="expiry-icon" aria-hidden="true">
                <svg class="icon icon--sm" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M12 3 4.5 6.5v5.2c0 4.5 3.1 7.8 7.5 9.3 4.4-1.5 7.5-4.8 7.5-9.3V6.5L12 3Z"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linejoin="round"
                  />
                  <path
                    d="m9.2 12 1.9 1.9 3.7-3.8"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <span class="expiry-text">
                The otp will expire in
                <strong>{{ formattedTimer }}</strong>
              </span>
            </p>

            <div class="resend-row">
              <button
                class="resend-link"
                type="button"
                :disabled="sending || timerSeconds > 0"
                @click="sendOtp"
              >
                {{ sending ? 'Sending…' : 'Resend OTP' }}
                <span class="resend-icon" aria-hidden="true">
                  <svg class="icon icon--xs" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                    />
                    <path
                      d="M19.5 5.5v4h-4"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
              </button>
              <button
                class="change-email"
                type="button"
                :disabled="loggingIn || sending"
                @click="backToEmail"
              >
                Change email
              </button>
            </div>
          </div>
        </transition>

        <p v-if="error" class="field-error" role="alert">{{ error }}</p>

        <button
          v-if="!otpSent"
          class="btn btn-primary"
          type="button"
          :disabled="sending || !identifier"
          @click="sendOtp"
        >
          <span v-if="sending" class="btn-spinner" aria-hidden="true" />
          <template v-else>
            SEND OTP
            <span class="btn-arrow" aria-hidden="true">→</span>
          </template>
        </button>

        <button
          v-else
          class="btn btn-primary"
          type="submit"
          :disabled="loggingIn || !otpComplete"
        >
          <span v-if="loggingIn" class="btn-spinner" aria-hidden="true" />
          <template v-else>
            VERIFY &amp; CONTINUE
            <span class="btn-arrow" aria-hidden="true">→</span>
          </template>
        </button>
      </form>
    </div>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="top" timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import { signIn, confirmSignIn, signOut, fetchUserAttributes } from 'aws-amplify/auth'
import rcsLogo from '../../assets/rcs_logo_background - Copy.png'
import {
  COGNITO_CLIENT_ID,
  COGNITO_REGION,
  COGNITO_USER_POOL_ID
} from '@/Configurations/cognito'

const OTP_SECONDS = 90
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  name: 'LoginPage',
  data() {
    return {
      rcsLogo,
      cognito: {
        userPoolId: COGNITO_USER_POOL_ID,
        clientId: COGNITO_CLIENT_ID,
        region: COGNITO_REGION
      },
      otpSent: false,
      identifier: '',
      otpDigits: ['', '', '', '', '', ''],
      otpRefs: [],
      emailFocused: false,
      sending: false,
      loggingIn: false,
      error: '',
      timerSeconds: 0,
      timerId: null,
      snackbar: {
        show: false,
        message: '',
        color: 'error'
      }
    }
  },
  computed: {
    otpComplete() {
      return this.otpDigits.every((digit) => /^\d$/.test(digit))
    },
    formattedTimer() {
      const minutes = String(Math.floor(this.timerSeconds / 60)).padStart(2, '0')
      const seconds = String(this.timerSeconds % 60).padStart(2, '0')
      return `${minutes}:${seconds}`
    }
  },
  beforeUnmount() {
    this.clearTimer()
  },
  methods: {
    setOtpRef(el, index) {
      if (el) {
        this.otpRefs[index] = el
      }
    },
    clearTimer() {
      if (this.timerId) {
        clearInterval(this.timerId)
        this.timerId = null
      }
    },
    startTimer() {
      this.clearTimer()
      this.timerSeconds = OTP_SECONDS
      this.timerId = setInterval(() => {
        if (this.timerSeconds <= 1) {
          this.timerSeconds = 0
          this.clearTimer()
          return
        }
        this.timerSeconds -= 1
      }, 1000)
    },
    validateEmail() {
      if (!this.identifier) {
        this.error = 'Please enter your email.'
        return false
      }
      if (!EMAIL_REGEX.test(this.identifier)) {
        this.error = 'Please enter a valid email address.'
        return false
      }
      return true
    },
    cognitoErrorMessage(error) {
      const name = error?.name || error?.code || ''
      const message = String(error?.message || '')

      if (
        name === 'UserNotFoundException' ||
        /USER_AUTH flow not enabled/i.test(message) ||
        /user does not exist/i.test(message)
      ) {
        return 'The agent does not exist'
      }
      if (name === 'CodeMismatchException') {
        return 'Invalid OTP. Please try again.'
      }
      if (name === 'ExpiredCodeException') {
        return 'This OTP has expired. Please resend the code.'
      }
      if (name === 'LimitExceededException' || name === 'TooManyRequestsException') {
        return 'Too many attempts. Please wait and try again.'
      }
      if (name === 'UserNotConfirmedException') {
        return 'This account is not confirmed yet.'
      }
      if (name === 'NotAuthorizedException') {
        if (/incorrect|invalid|code/i.test(message)) {
          return 'Invalid OTP. Please try again.'
        }
        return 'This email is not authorized to sign in.'
      }
      if (message.includes('already a signed in user')) {
        return 'You are already signed in. Please continue.'
      }
      return message || 'Could not complete sign in. Please try again.'
    },
    showLoginSnackbar(message) {
      this.snackbar = {
        show: true,
        message,
        color: 'error'
      }
    },
    async clearExistingSession() {
      try {
        await signOut()
      } catch (error) {
        // No existing session
      }
    },
    async handleSignInNextStep(output) {
      const step = output?.nextStep?.signInStep

      if (step === 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION') {
        const challenges = output.nextStep.availableChallenges || []
        const preferred = challenges.includes('EMAIL_OTP')
          ? 'EMAIL_OTP'
          : challenges.find((item) => String(item).includes('OTP') || String(item).includes('EMAIL'))
            || challenges[0]

        if (!preferred) {
          throw new Error('No OTP challenge is available for this account.')
        }

        return this.handleSignInNextStep(
          await confirmSignIn({ challengeResponse: preferred })
        )
      }

      if (
        step === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE' ||
        step === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' ||
        step === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE' ||
        step === 'CONFIRM_SIGN_IN_WITH_OTP' ||
        step === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE'
      ) {
        return output
      }

      if (output?.isSignedIn || step === 'DONE') {
        return output
      }

      return output
    },
    async startCognitoOtp() {
      const username = this.identifier
      let firstError = null

      try {
        const output = await signIn({
          username,
          options: {
            authFlowType: 'USER_AUTH',
            preferredChallenge: 'EMAIL_OTP'
          }
        })
        return this.handleSignInNextStep(output)
      } catch (error) {
        firstError = error
      }

      try {
        const output = await signIn({
          username,
          options: {
            authFlowType: 'CUSTOM_WITHOUT_SRP'
          }
        })
        return this.handleSignInNextStep(output)
      } catch (error) {
        throw firstError || error
      }
    },
    async sendOtp() {
      this.error = ''
      if (!this.validateEmail()) return

      this.sending = true
      try {
        await this.clearExistingSession()
        await this.startCognitoOtp()
        this.otpSent = true
        this.otpDigits = ['', '', '', '', '', '']
        this.startTimer()
        this.$nextTick(() => {
          this.otpRefs[0]?.focus()
        })
      } catch (e) {
        const mapped = this.cognitoErrorMessage(e) || 'Could not send OTP. Please try again.'
        if (mapped === 'The agent does not exist') {
          this.error = ''
          this.showLoginSnackbar(mapped)
        } else {
          this.error = mapped
        }
      } finally {
        this.sending = false
      }
    },
    backToEmail() {
      this.otpSent = false
      this.otpDigits = ['', '', '', '', '', '']
      this.error = ''
      this.clearTimer()
      this.timerSeconds = 0
      this.$nextTick(() => {
        this.$refs.emailInput?.focus()
      })
    },
    onOtpInput(event, index) {
      const raw = event.target.value.replace(/\D/g, '')
      const digit = raw.slice(-1)
      this.otpDigits[index] = digit
      event.target.value = digit
      this.error = ''

      if (digit && index < 5) {
        this.otpRefs[index + 1]?.focus()
      }
    },
    onOtpKeydown(event, index) {
      if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
        this.otpRefs[index - 1]?.focus()
      }
      if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault()
        this.otpRefs[index - 1]?.focus()
      }
      if (event.key === 'ArrowRight' && index < 5) {
        event.preventDefault()
        this.otpRefs[index + 1]?.focus()
      }
    },
    onOtpPaste(event) {
      event.preventDefault()
      const pasted = (event.clipboardData?.getData('text') || '')
        .replace(/\D/g, '')
        .slice(0, 6)

      if (!pasted) return

      this.otpDigits = Array.from({ length: 6 }, (_, i) => pasted[i] || '')
      const focusIndex = Math.min(pasted.length, 5)
      this.$nextTick(() => {
        this.otpRefs[focusIndex]?.focus()
      })
    },
    async onSubmit() {
      if (!this.otpSent || !this.otpComplete) return

      this.error = ''
      this.loggingIn = true
      try {
        const code = this.otpDigits.join('')
        const output = await confirmSignIn({
          challengeResponse: code
        })

        const completed = output?.isSignedIn || output?.nextStep?.signInStep === 'DONE'
        if (!completed) {
          throw new Error('Invalid OTP. Please try again.')
        }

        let email = this.identifier
        try {
          const attributes = await fetchUserAttributes()
          email = attributes?.email || this.identifier
        } catch (attributeError) {
          console.error('Failed to load user attributes:', attributeError)
        }

        this.$store.dispatch('login', email)
        this.$router.push({ name: 'home' })
      } catch (e) {
        const mapped = this.cognitoErrorMessage(e) || 'Invalid OTP. Please try again.'
        if (mapped === 'The agent does not exist') {
          this.error = ''
          this.showLoginSnackbar(mapped)
        } else {
          this.error = mapped
        }
      } finally {
        this.loggingIn = false
      }
    }
  }
}
</script>

<style scoped>
.login-page {
  --rcs-blue: #0056d2;
  --rcs-blue-deep: #0047b3;
  --rcs-blue-soft: #e8f1ff;
  --rcs-ink: #1f2937;
  --rcs-muted: #6b7280;
  --rcs-line: #d1d5db;
  --rcs-card: #ffffff;

  /* Fluid tokens scale from phone → TV */
  --page-pad-x: clamp(0.75rem, 2.5vw, 2rem);
  --page-pad-y: clamp(0.75rem, 2.5vh, 1.75rem);
  --card-width: min(100%, 26rem);
  --card-pad-x: clamp(1rem, 3vw, 1.75rem);
  --card-pad-y: clamp(1.1rem, 2.5vh, 1.6rem);
  --card-radius: clamp(0.75rem, 1.5vw, 1rem);
  --logo-width: clamp(8.5rem, 36vw, 11.5rem);
  --welcome-size: clamp(1.2rem, 3.2vw, 1.45rem);
  --title-size: clamp(0.88rem, 2vw, 0.98rem);
  --field-height: 2.85rem;
  --body-size: 0.94rem;
  --label-size: 0.78rem;
  --otp-gap: clamp(0.3rem, 1.4vw, 0.55rem);
  --otp-font: clamp(1rem, 2.8vw, 1.2rem);
  --btn-font: 0.88rem;
  --icon-size: 1.1rem;

  box-sizing: border-box;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  display: grid;
  place-items: center;
  padding:
    max(var(--page-pad-y), env(safe-area-inset-top))
    max(var(--page-pad-x), env(safe-area-inset-right))
    max(var(--page-pad-y), env(safe-area-inset-bottom))
    max(var(--page-pad-x), env(safe-area-inset-left));
  background:
    radial-gradient(circle at 15% 20%, rgba(0, 86, 210, 0.08), transparent 40%),
    radial-gradient(circle at 85% 80%, rgba(0, 86, 210, 0.1), transparent 42%),
    linear-gradient(180deg, #f3f6fb 0%, #eef3f9 100%);
  font-family: 'Manrope', 'Segoe UI', sans-serif;
  font-size: var(--body-size);
  color: var(--rcs-ink);
  overflow-x: hidden;
  -webkit-tap-highlight-color: transparent;
}

.login-card {
  box-sizing: border-box;
  width: var(--card-width);
  max-width: 100%;
  background: var(--rcs-card);
  border: 1px solid #e5e7eb;
  border-radius: var(--card-radius);
  box-shadow:
    0 18px 45px rgba(15, 23, 42, 0.08),
    0 2px 8px rgba(15, 23, 42, 0.04);
  padding: var(--card-pad-y) var(--card-pad-x);
  text-align: center;
  animation: card-in 0.55s ease-out both;
}

.login-logo {
  width: var(--logo-width);
  max-width: 70%;
  height: auto;
  display: block;
  margin: 0 auto 0.65rem;
  animation: logo-in 0.7s ease-out both;
}

.welcome-title {
  margin: 0 0 0.2rem;
  font-size: var(--welcome-size);
  font-weight: 750;
  letter-spacing: -0.02em;
  color: #16325c;
  line-height: 1.2;
  text-align: center;
}

.step-title {
  margin: 0 0 1rem;
  font-size: var(--title-size);
  font-weight: 500;
  color: #6b7280;
  letter-spacing: -0.01em;
  text-align: center;
  line-height: 1.35;
}

.login-form {
  display: flex;
  flex-direction: column;
  text-align: left;
  width: 100%;
}

.field {
  position: relative;
  width: 100%;
}

.field-label {
  position: absolute;
  top: 0;
  left: 0.85rem;
  z-index: 1;
  transform: translateY(-50%);
  padding: 0 0.35rem;
  background: #fff;
  font-size: var(--label-size);
  font-weight: 500;
  color: #6b7280;
  pointer-events: none;
  line-height: 1;
}

.field--focused .field-label {
  color: var(--rcs-ink);
}

.input-wrap {
  position: relative;
  height: var(--field-height);
}

.input-icon {
  position: absolute;
  left: clamp(0.7rem, 2vw, 0.95rem);
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  display: inline-flex;
  pointer-events: none;
}

.icon {
  width: var(--icon-size);
  height: var(--icon-size);
  display: block;
}

.icon--sm {
  width: clamp(0.9rem, 2vw, 1.1rem);
  height: clamp(0.9rem, 2vw, 1.1rem);
}

.icon--xs {
  width: clamp(0.8rem, 1.8vw, 0.95rem);
  height: clamp(0.8rem, 1.8vw, 0.95rem);
}

.field-input {
  width: 100%;
  height: var(--field-height);
  box-sizing: border-box;
  border: 1.5px solid var(--rcs-line);
  border-radius: clamp(0.4rem, 1vw, 0.55rem);
  background: #fff;
  color: var(--rcs-ink);
  padding: 0 0.95rem 0 clamp(2.4rem, 6vw, 2.9rem);
  font: inherit;
  font-size: max(16px, var(--body-size));
  outline: none;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.field--focused .field-input {
  border-color: #111827;
  background: #f5f9ff;
}

.field-input:disabled,
.field-input[readonly] {
  opacity: 0.9;
  background: #f9fafb;
  cursor: default;
}

.field-error {
  margin: 0.85rem 0 0;
  color: #dc2626;
  font-size: clamp(0.8rem, 1.6vw, 0.92rem);
  text-align: left;
  width: 100%;
}

.otp-section {
  margin-top: 0.9rem;
  overflow: hidden;
}

.otp-label {
  display: block;
  width: 100%;
  margin: 0 0 0.55rem;
  font-size: var(--label-size);
  font-weight: 600;
  color: #9ca3af;
  text-align: left;
}

.otp-row {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: var(--otp-gap);
  width: 100%;
}

.otp-input {
  width: 100%;
  min-width: 0;
  aspect-ratio: 1;
  max-height: 4.25rem;
  box-sizing: border-box;
  border: 1.5px solid #d1d5db;
  border-radius: clamp(0.45rem, 1.2vw, 0.75rem);
  background: #fff;
  color: var(--rcs-ink);
  text-align: center;
  font-size: max(16px, var(--otp-font));
  font-weight: 700;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
  caret-color: var(--rcs-blue);
}

.otp-input::placeholder {
  color: #c4c9d2;
  font-weight: 500;
}

.otp-input:focus {
  border-color: var(--rcs-blue);
  box-shadow: 0 0 0 3px rgba(0, 86, 210, 0.14);
  transform: translateY(-1px);
}

.expiry-row {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.35rem 0.45rem;
  margin: clamp(0.7rem, 1.8vh, 1rem) 0 0;
  color: var(--rcs-muted);
  font-size: clamp(0.78rem, 1.6vw, 0.92rem);
  text-align: center;
}

.expiry-icon {
  display: inline-flex;
  color: var(--rcs-blue);
  flex-shrink: 0;
}

.expiry-text strong {
  color: var(--rcs-blue);
  font-weight: 700;
  margin-left: 0.2rem;
}

.resend-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.55rem 0.75rem;
  margin-top: clamp(0.7rem, 1.8vh, 0.95rem);
}

.resend-link,
.change-email {
  min-height: 2.5rem;
  display: inline-flex;
  align-items: center;
  touch-action: manipulation;
}

.resend-link {
  gap: 0.25rem;
  border: 0;
  background: transparent;
  color: var(--rcs-blue);
  font: inherit;
  font-size: clamp(0.78rem, 1.6vw, 0.9rem);
  font-weight: 700;
  cursor: pointer;
  padding: 0.25rem 0;
}

.resend-link:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.resend-icon {
  display: inline-flex;
}

.change-email {
  border: 0;
  background: transparent;
  color: #9ca3af;
  font: inherit;
  font-size: clamp(0.76rem, 1.5vw, 0.88rem);
  font-weight: 600;
  cursor: pointer;
  padding: 0.25rem 0;
}

.change-email:hover:not(:disabled) {
  color: var(--rcs-blue);
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  width: 100%;
  min-height: var(--field-height);
  height: var(--field-height);
  margin-top: 0.95rem;
  border: 0;
  border-radius: clamp(0.4rem, 1vw, 0.55rem);
  padding: 0 1rem;
  font: inherit;
  font-size: var(--btn-font);
  font-weight: 700;
  letter-spacing: 0.05em;
  cursor: pointer;
  touch-action: manipulation;
  transition: transform 0.15s ease, filter 0.15s ease, opacity 0.15s ease;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  color: #fff;
  background: var(--rcs-blue);
  box-shadow: 0 8px 18px rgba(0, 86, 210, 0.25);
}

@media (hover: hover) and (pointer: fine) {
  .btn-primary:not(:disabled):hover {
    filter: brightness(1.05);
    transform: translateY(-1px);
  }
}

.btn-primary:not(:disabled):active {
  transform: translateY(0);
  filter: brightness(0.97);
}

.btn-arrow {
  font-size: 1.05em;
  line-height: 1;
}

.btn-spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.otp-slide-enter-active,
.otp-slide-leave-active {
  transition: opacity 0.28s ease, transform 0.28s ease, max-height 0.28s ease;
  max-height: 20rem;
}

.otp-slide-enter-from,
.otp-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
  max-height: 0;
}

@keyframes card-in {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes logo-in {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Extra-small phones */
@media (max-width: 359px) {
  .login-page {
    --card-pad-x: 0.85rem;
    --otp-gap: 0.22rem;
    --otp-font: 0.95rem;
  }

  .resend-row {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* Phones */
@media (max-width: 480px) {
  .login-page {
    align-items: stretch;
    padding-top: max(0.75rem, env(safe-area-inset-top));
    padding-bottom: max(0.75rem, env(safe-area-inset-bottom));
  }

  .login-card {
    margin: auto 0;
    width: 100%;
    box-shadow:
      0 12px 28px rgba(15, 23, 42, 0.07),
      0 1px 4px rgba(15, 23, 42, 0.04);
  }
}

/* Tablets */
@media (min-width: 600px) and (max-width: 1023px) {
  .login-page {
    --card-width: min(100%, 26.5rem);
  }
}

/* Laptops / desktops */
@media (min-width: 1024px) {
  .login-page {
    --card-width: min(100%, 26rem);
  }
}

/* Large monitors / TVs — scale gently, keep compact */
@media (min-width: 1440px) {
  .login-page {
    --card-width: min(100%, 28rem);
    --logo-width: 12rem;
    --welcome-size: 1.5rem;
    --title-size: 1rem;
    --field-height: 3rem;
    --body-size: 0.96rem;
    --otp-font: 1.2rem;
  }
}

@media (min-width: 1920px) {
  .login-page {
    --card-width: min(100%, 30rem);
    --card-pad-x: 1.85rem;
    --card-pad-y: 1.75rem;
    --logo-width: 12.5rem;
    --welcome-size: 1.55rem;
    --field-height: 3.1rem;
    --title-size: 1.02rem;
    --body-size: 0.98rem;
    --label-size: 0.8rem;
    --otp-font: 1.25rem;
    --btn-font: 0.9rem;
  }
}

/* Short landscape phones / foldables */
@media (max-height: 520px) and (orientation: landscape) {
  .login-page {
    align-items: flex-start;
    overflow-y: auto;
    --page-pad-y: 0.5rem;
    --card-pad-y: 0.75rem;
    --logo-width: clamp(7rem, 20vh, 9rem);
  }

  .login-logo {
    margin-bottom: 0.4rem;
  }

  .welcome-title {
    margin-bottom: 0.1rem;
  }

  .step-title {
    margin-bottom: 0.65rem;
  }

  .otp-input {
    aspect-ratio: 1.15 / 1;
    max-height: 2.75rem;
  }
}

/* Prefer reduced motion */
@media (prefers-reduced-motion: reduce) {
  .login-card,
  .login-logo,
  .otp-slide-enter-active,
  .otp-slide-leave-active,
  .btn,
  .otp-input,
  .field-input {
    animation: none !important;
    transition: none !important;
  }
}
</style>
